import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Download, FileSpreadsheet, Upload } from "lucide-react";
import { LinearProgress, Step, StepLabel, Stepper } from "@mui/material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Table,
  Typography,
} from "../ds";
import { ImportColumnMatching, ImportValueReview } from "./ImportMappingReview";
import type { DataTableColumn } from "../ds/Table";
import {
  commitImport,
  validateImport,
  ImportBatchError,
  mapSuggestionImportColumns,
} from "../api/admin";
import { invalidateSuggestionCaches } from "../api/ess";
import { useDepartments } from "../hooks/useDepartments";
import { useWorkflowConfig, statusLabel } from "../hooks/useWorkflow";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { CATEGORIES } from "../types/ess";
import type { ImportCommitResult, ImportRow } from "../types/admin";
import { apiErrorMessage } from "../utils/apiError";
import {
  IMPORT_FIELDS,
  buildImportRows,
  downloadImportTemplate,
  guessColumns,
  readImportSheet,
  type ColumnMapping,
  type ImportSheet,
  type ValueResolutions,
} from "../utils/suggestionImport";

interface Props {
  open: boolean;
  onClose: () => void;
  onImported?: (ids: string[]) => void;
}
const STEPS = [
  "Upload",
  "Match columns",
  "Review values",
  "Preview",
  "Import results",
];

// The parent mounts a new session on each open; asynchronous work cannot alter a reopened wizard.
export default function ImportSuggestionsDialog(props: Props) {
  return props.open ? <ImportSession {...props} /> : null;
}
function ImportSession({ onClose, onImported }: Props) {
  const qc = useQueryClient();
  const { isAdmin } = useIsAdmin();
  const departmentQuery = useDepartments();
  const departments = useMemo(
    () => ({
      names: Array.from(
        new Set((departmentQuery.data ?? []).map((item) => item.name)),
      ).sort(),
      isLoading: departmentQuery.isLoading,
      isError: departmentQuery.isError,
    }),
    [departmentQuery.data, departmentQuery.isLoading, departmentQuery.isError],
  );
  const workflow = useWorkflowConfig();
  const [step, setStep] = useState(0);
  const [sheet, setSheet] = useState<ImportSheet>();
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [resolutions, setResolutions] = useState<ValueResolutions>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mappingNotice, setMappingNotice] = useState("");
  const [discard, setDiscard] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [result, setResult] = useState<ImportCommitResult>();
  const [uncertain, setUncertain] = useState<number[]>([]);
  const [stopped, setStopped] = useState(false);
  const [progress, setProgress] = useState(0);
  const [serverErrors, setServerErrors] = useState<Record<number, string[]>>({});
  const [checked, setChecked] = useState(false);
  const [checking, setChecking] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const input = useRef<HTMLInputElement>(null);
  const options = useMemo(
    () => ({
      department: departments.names.map((value) => ({ value, label: value })),
      category: CATEGORIES.map((value) => ({ value, label: value })),
      status: Object.keys(workflow.data?.states ?? {}).map((value) => ({
        value,
        label: statusLabel(workflow.data, value),
      })),
    }),
    [departments.names, workflow.data],
  );
  const localRows = useMemo(
    () => (sheet ? buildImportRows(sheet, mapping, resolutions, options) : []),
    [sheet, mapping, resolutions, options],
  );
  const rows = useMemo(() => localRows.map(row => ({
    ...row, errors: [...row.errors, ...(serverErrors[row.row_number] ?? [])],
  })), [localRows, serverErrors]);
  const resetValidation = () => {
    setServerErrors({});
    setChecked(false);
  };
  const checkRows = async (candidates: ImportRow[]) => {
    setChecking(true);
    try {
      const validation = await validateImport(candidates);
      setServerErrors(previous => ({ ...previous, ...Object.fromEntries(
        validation.results.map(row => [row.row_number, row.errors]),
      ) }));
      setChecked(true);
      return validation;
    } finally {
      setChecking(false);
    }
  };
  const ready = rows.filter((row) => !row.errors.length);
  const missing = IMPORT_FIELDS.filter((f) => f.required && !mapping[f.key]);
  const catalogsReady =
    !!workflow.data && !departments.isLoading && departments.names.length > 0;
  const close = () => {
    if (busy) return;
    if (sheet && step < 4) setDiscard(true);
    else onClose();
  };

  const upload = async (file: File) => {
    if (!isAdmin || busy) return;
    setBusy(true);
    setError("");
    setMappingNotice("");
    resetValidation();
    setResolutions({});
    setAttempted(false);
    try {
      const parsed = await readImportSheet(file);
      setSheet(parsed);
      setMapping(guessColumns(parsed.columns));
      setStep(1);
      const samples = parsed.rows
        .slice(0, 5)
        .map((row) =>
          Object.fromEntries(
            Object.entries(row.values).map(([key, value]) => [
              key,
              (value as { text: string }).text,
            ]),
          ),
        );
      try {
        const ai = await mapSuggestionImportColumns(parsed.columns, samples);
        const safe: ColumnMapping = {};
        IMPORT_FIELDS.forEach((field) => {
          const column = ai[field.key];
          if (column && parsed.columns.includes(column))
            safe[field.key] = column;
        });
        setMapping({ ...guessColumns(parsed.columns), ...safe });
        setMappingNotice(
          "Columns matched automatically. Review each match before continuing.",
        );
      } catch {
        setMappingNotice(
          "Automatic matching was unavailable. We matched known headers; choose the remaining columns below.",
        );
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Could not read the workbook"));
    } finally {
      setBusy(false);
    }
  };
  const importRows = async () => {
    setAttempted(true);
    if (!ready.length || !catalogsReady || !isAdmin || !checked || busy) return;
    setBusy(true);
    setError("");
    setProgress(0);
    let completed: ImportCommitResult;
    try {
      const validation = await checkRows(ready);
      if (validation.results.some(row => row.errors.length)) {
        setError("Some rows are no longer ready. Review the updated errors before importing.");
        setBusy(false);
        return;
      }
      completed = await commitImport(rows, (partial) => {
        setProgress(partial.imported + partial.skipped);
      });
    } catch (err) {
      if (err instanceof ImportBatchError) {
        completed = err.completed;
        setUncertain(err.uncertainRows);
        setStopped(true);
        setError(err.message);
      } else {
        setError(apiErrorMessage(err, "Could not import"));
        setBusy(false);
        return;
      }
    }
    setResult(completed);
    setStep(4);
    setPage(0);
    setBusy(false);
    // Even an uncertain first batch may have committed on the server.
    invalidateSuggestionCaches(qc);
    const ids = completed.results.flatMap((row) =>
      row.suggestion_id && !row.errors.length ? [row.suggestion_id] : [],
    );
    if (ids.length) onImported?.(ids);
  };
  const outcomes = new Map(result?.results.map((row) => [row.row_number, row]));
  const displayed =
    step === 4
      ? rows.map((row) => {
          const outcome = outcomes.get(row.row_number);
          let errors = row.errors;
          if (outcome)
            errors = outcome.errors.length ? outcome.errors : ["Imported"];
          else if (uncertain.includes(row.row_number))
            errors = [
              "Outcome uncertain — check the suggestions list before retrying",
            ];
          else if (!errors.length) errors = ["Not sent"];
          return { ...row, errors };
        })
      : rows;
  const rowTone = (row: ImportRow): "error" | "warning" | undefined => {
    if (!row.errors.length || row.errors[0] === "Imported") return undefined;
    if (
      step === 4 &&
      (uncertain.includes(row.row_number) || row.errors[0] === "Not sent")
    )
      return "warning";
    return "error";
  };
  const processed = result ? result.imported + result.skipped : progress;
  const progressPercent = ready.length
    ? Math.min(100, Math.floor((processed / ready.length) * 100))
    : 0;
  const columns: DataTableColumn<ImportRow>[] = [
    { field: "row_number", header: "Row", width: 60 },
    {
      field: "employee_name",
      header: "Employee",
      width: 160,
      render: (row) => (
        <Box>
          {row.employee_name}
          <Typography variant="caption"> · {row.employee_id}</Typography>
        </Box>
      ),
    },
    { field: "title", header: "Title", width: 200, tooltip: true },
    { field: "department", header: "Department", width: 130 },
    { field: "category", header: "Category", width: 160 },
    {
      field: "status",
      header: "Status",
      width: 150,
      render: (row) => statusLabel(workflow.data, row.status),
    },
    { field: "created_at", header: "Submitted", width: 110 },
    { field: "suggestion_number", header: "Suggestion #", width: 140 },
    { field: "description", header: "Description", width: 280, tooltip: true },
    { field: "submitted_on_behalf_of", header: "On behalf of", width: 160 },
    { field: "other_category", header: "Other category", width: 160 },
    { field: "remarks", header: "Remarks", width: 200, tooltip: true },
    {
      field: "errors",
      header: "Validation / result",
      width: 280,
      render: (row) => (
        <Typography
          color={
            row.errors.length && row.errors[0] !== "Imported"
              ? "error.main"
              : "success.main"
          }
          variant="body2"
        >
          {row.errors.join(" · ") || "Ready"}
        </Typography>
      ),
    },
  ];
  const next = async () => {
    if (busy) return;
    setAttempted(true);
    if (step === 1 && missing.length) return;
    if (step === 2) {
      if (!catalogsReady || !isAdmin) return;
      setBusy(true);
      setError("");
      try {
        await checkRows(localRows);
      } catch (err) {
        setError(apiErrorMessage(err, "Could not check suggestions. Try again before importing."));
        return;
      } finally {
        setBusy(false);
      }
    }
    setAttempted(false);
    setPage(0);
    setStep(step + 1);
  };
  return (
    <>
      <Modal
        open
        title="Import suggestions"
        maxWidth={step >= 3 ? "lg" : "md"}
        onClose={close}
        actions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ flex: 1 }}
            >
              <Button onClick={close} disabled={busy}>
                {step === 4 ? "Done" : "Cancel"}
              </Button>
              {step > 0 && step < 4 && (
                <Button
                  onClick={() => {
                    resetValidation();
                    setStep(step - 1);
                    setAttempted(false);
                    setError("");
                  }}
                  disabled={busy}
                >
                  Back
                </Button>
              )}
            </Stack>
            {step > 0 && step < 3 && (
              <Button variant="contained" onClick={next} disabled={busy}>
                {checking ? "Checking suggestions…" : "Continue"}
              </Button>
            )}
            {step === 3 && (
              <Button variant="contained" onClick={importRows} disabled={busy || !checked || !ready.length}>
                {checking ? "Checking suggestions…" : busy
                  ? `Importing… ${progress} rows processed`
                  : `Accept & Import ${ready.length}`}
              </Button>
            )}
          </Stack>
        }
      >
        <Stack spacing={2}>
          <Stepper
            activeStep={step}
            alternativeLabel
            sx={{ display: { xs: "none", sm: "flex" }, pt: 1 }}
          >
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{ display: { xs: "block", sm: "none" } }}
          >
            Step {step + 1} of {STEPS.length} · {STEPS[step]}
          </Typography>
          {sheet && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                p: 1.25,
                bgcolor: "background.default",
                borderRadius: 1.5,
                minWidth: 0,
              }}
            >
              <RavenIcon
                icon={FileSpreadsheet}
                sx={{ color: "text.secondary", flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                title={sheet.name}
                noWrap
                sx={{ flex: 1 }}
              >
                {sheet.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0 }}
              >
                {sheet.rows.length} rows
              </Typography>
            </Stack>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {((busy && !checking && step === 3) || (step === 4 && result)) && (
            <Stack
              spacing={1}
              role="status"
              aria-live="polite"
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.default",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={0.5}
              >
                <Typography variant="subtitle2">
                  {busy
                    ? "Importing suggestions"
                    : stopped
                      ? "Import stopped"
                      : "Import complete"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {processed} of {ready.length} rows processed
                </Typography>
              </Stack>
              <LinearProgress
                aria-label="Suggestion import progress"
                variant={
                  busy && processed === 0 ? "indeterminate" : "determinate"
                }
                value={progressPercent}
                color={stopped ? "warning" : "primary"}
                sx={{ height: 8, borderRadius: 999 }}
              />
              <Typography variant="caption" color="text.secondary">
                {busy
                  ? "Progress updates after each batch is confirmed. Please keep this window open."
                  : stopped
                    ? "Check uncertain rows in the suggestions list before importing again."
                    : "All submitted rows have been processed."}
              </Typography>
            </Stack>
          )}
          {busy && (step !== 3 || checking) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography role="status">
                {checking ? "Checking existing suggestions…" : step === 1 ? "Matching columns…" : "Reading workbook…"}
              </Typography>
            </Stack>
          )}
          {!isAdmin && (
            <Alert severity="error">
              Only administrators can import historical suggestions.
            </Alert>
          )}
          {(departments.isError || workflow.isError) && (
            <Alert severity="error">
              Could not load departments or workflow statuses.{" "}
              <Button
                onClick={() => {
                  workflow.refetch();
                  qc.invalidateQueries({ queryKey: ["departments"] });
                }}
              >
                Retry
              </Button>
            </Alert>
          )}
          {step === 0 && (
            <Stack spacing={2.5}>
              <Stack
                spacing={2}
                alignItems="center"
                sx={{
                  p: { xs: 2.5, sm: 4 },
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.default",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    placeItems: "center",
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    color: "primary.main",
                  }}
                >
                  <RavenIcon icon={FileSpreadsheet} sx={{ fontSize: 28 }} />
                </Box>
                <Stack spacing={0.75}>
                  <Typography variant="h6">Upload your suggestions</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 440 }}
                  >
                    Select an Excel file from your computer. You can match its
                    columns and review the suggestions before importing.
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<RavenIcon icon={Upload} />}
                  disabled={busy || !isAdmin}
                  onClick={() => input.current?.click()}
                  fullWidth
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Upload Excel file
                </Button>
                <Typography variant="caption" color="text.secondary">
                  .xlsx or .xls · Put column headers in the first row of the
                  first sheet.
                </Typography>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">
                    Need a starting file?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Download the template, add your suggestions, then upload it
                    here.
                  </Typography>
                </Stack>
                <Button
                  variant="outlined"
                  startIcon={<RavenIcon icon={Download} />}
                  sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
                  onClick={() =>
                    downloadImportTemplate(
                      Object.keys(workflow.data?.states ?? {}),
                    ).catch((err) =>
                      setError(
                        apiErrorMessage(err, "Could not download template"),
                      ),
                    )
                  }
                >
                  Download Excel template
                </Button>
              </Stack>
              <input
                ref={input}
                hidden
                type="file"
                accept=".xlsx,.xls"
                disabled={busy || !isAdmin}
                aria-label="Upload suggestions workbook"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) upload(file);
                }}
              />
            </Stack>
          )}
          {step === 1 && sheet && (
            <>
              {attempted && missing.length > 0 && (
                <Alert severity="error">
                  Match these required fields:{" "}
                  {missing.map((f) => f.label).join(", ")}
                </Alert>
              )}
              <ImportColumnMatching
                sheet={sheet}
                mapping={mapping}
                busy={busy}
                attempted={attempted}
                notice={mappingNotice}
                onChange={(field, column) => {
                  setMapping((previous) => ({ ...previous, [field]: column }));
                  setResolutions({});
                }}
              />
            </>
          )}
          {step === 2 && sheet && (
            <ImportValueReview
              disabled={busy}
              sheet={sheet}
              mapping={mapping}
              resolutions={resolutions}
              options={options}
              onEditColumns={() => {
                resetValidation();
                setStep(1);
                setAttempted(false);
                setError("");
              }}
              onChange={(key, value) => {
                resetValidation();
                setResolutions((previous) => ({ ...previous, [key]: value }));
              }}
            />
          )}
          {step === 3 && (
            <>
              <Alert
                severity={rows.length !== ready.length ? "warning" : "info"}
              >
                {ready.length} ready · {rows.length - ready.length} blocked.
                Checked against existing suggestions on the server. Only ready rows will be imported. Blank dates use the import
                date; blank suggestion numbers are generated.
              </Alert>
              {!catalogsReady && (
                <Alert severity="warning">
                  Departments and workflow statuses must load before importing.
                </Alert>
              )}
              {attempted && !ready.length && (
                <Alert severity="error">
                  No rows are ready. Go back to resolve values or correct the
                  workbook.
                </Alert>
              )}
            </>
          )}
          {step === 4 && result && (
            <Alert
              severity={
                result.skipped || stopped || rows.length > result.imported
                  ? "warning"
                  : "success"
              }
            >
              {result.imported} imported · {result.skipped} rejected by the
              server · {rows.filter((row) => row.errors.length).length} blocked
              before import.{" "}
              {stopped && "Import stopped before all batches completed."}
            </Alert>
          )}
          {step >= 3 && (
            <Box sx={{ maxHeight: 480, overflow: "auto" }}>
              <Table
                columns={columns}
                rows={displayed.slice(page * pageSize, (page + 1) * pageSize)}
                rowKey="row_number"
                getRowTone={rowTone}
                page={page}
                rowsPerPage={pageSize}
                total={displayed.length}
                onPageChange={setPage}
                onRowsPerPageChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
              />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary">
            Original dates and statuses are preserved. Workflow emails are not
            sent.
          </Typography>
        </Stack>
      </Modal>
      <Modal
        open={discard}
        title="Discard this import?"
        onClose={() => setDiscard(false)}
        actions={
          <>
            <Button onClick={() => setDiscard(false)}>Keep reviewing</Button>
            <Button color="error" onClick={onClose}>
              Discard
            </Button>
          </>
        }
      >
        <Typography>
          Your column matches and reviewed values will be discarded.
        </Typography>
      </Modal>
    </>
  );
}
